import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import nb_skjematekst from "./nb/skjematekst";
import nb_validering from "./nb/valideringsmeldinger";
import nb_feedback from "./nb/feedback";
import nn_feedback from "./nn/feedback";
import en_feedback from "./en/feedback";
import { logFrontendToBackend } from "../feil/logFrontendToBackend";
import { LoggLevel } from "../api/apiTypes";
import nb_default_validering from "./nb/fallbackValideringsmeldinger";
import nb_incidents from "./nb/incidents";
import nb_samtykke from "./nb/samtykke";
import nn_skjematekst from "./nn/skjematekst";
import nn_validering from "./nn/valideringsmeldinger";
import nn_default_validering from "./nn/fallbackValideringsmeldinger";
import nn_incidents from "./nn/incidents";
import nn_samtykke from "./nn/samtykke";
import en_skjematekst from "./en/skjematekst";
import en_validering from "./en/valideringsmeldinger";
import en_default_validering from "./en/fallbackValideringsmeldinger";
import en_incidents from "./en/incidents";
import en_samtykke from "./en/samtykke";
import { CheckName } from "../types/incidentbanner.typer";
import { AnmeldelseType } from "@politiet/anmeldelse-lib/types/anmeldelseUtkast.typer";

export const Languages = {
  nb: "nb",
  nn: "nn",
  en: "en",
} as const;

export type Languages = (typeof Languages)[keyof typeof Languages];

export const toLanguage = (
  value: string | null | undefined,
  fallback: Languages = Languages.nb,
): Languages => {
  switch (value ?? undefined) {
    case Languages.nb:
      return Languages.nb;
    case Languages.nn:
      return Languages.nn;
    case Languages.en:
      return Languages.en;
    default:
      return fallback;
  }
};

export const defaultNS = "skjematekst";
export const resources = {
  nb: {
    skjematekst: nb_skjematekst,
    validering: nb_validering,
    default_validering: nb_default_validering,
    incidents: nb_incidents,
    samtykke: nb_samtykke,
    feedback: nb_feedback,
  },
  nn: {
    skjematekst: nn_skjematekst,
    validering: nn_validering,
    default_validering: nn_default_validering,
    incidents: nn_incidents,
    samtykke: nn_samtykke,
    feedback: nn_feedback,
  },
  en: {
    skjematekst: en_skjematekst,
    validering: en_validering,
    default_validering: en_default_validering,
    incidents: en_incidents,
    samtykke: en_samtykke,
    feedback: en_feedback,
  },
};

// Array som holder på hvilke missing keys vi har fått inn. Dette så vi ikke logger samme key flere ganger
// Verdier som ligger inne fra før vil ikke logges til backend
const missingKeysReported: string[] = [
  "hendelsesbeskrivelse.handlinger.innbrudd.ikke.null",
  "hendelsesbeskrivelse.handlinger.skadeverk.gjenstander.fartoy.ikke.null",
  "hendelsesbeskrivelse.handlinger.tyveri.gjenstander.elektronikk.ikke.null",
  "hendelsesbeskrivelse.handlinger.tyveri.gjenstander.fartoy.ikke.null",
  "hendelsesbeskrivelse.handlinger.tyveri.gjenstander.kjennemerke.ikke.null",
  "hendelsesbeskrivelse.handlinger.tyveri.gjenstander.mobiltelefon.ikke.null",
  "hendelsesbeskrivelse.handlinger.tyveri.gjenstander.mobiltelefon.imei.maks.lengde",
  "hendelsesbeskrivelse.handlinger.tyveri.gjenstander.sykkel.ikke.null",
  "hendelsesbeskrivelse.handlinger.tyveri.gjenstander.sykkel.merke.ikke.blank",
];

const fallbackMessage: string = "<Feil ved henting av tekst>";

export const initI18n = () => {
  i18next
    .use(initReactI18next)
    .init({
      lng: localStorage.getItem("lng") || "nb", // if you're using a language detector, do not define the lng option
      debug: true,
      resources,
      fallbackLng: "nb",
      defaultNS,
      contextSeparator: "_",
      saveMissing: true,
      missingKeyHandler: (_lngs, ns, key) => {
        if (!missingKeysReported.includes(key)) {
          missingKeysReported.push(key);
          logFrontendToBackend(
            new Error(`Missing translation key - ns: ${ns} - key: ${key}`),
            LoggLevel.ERROR,
          );
        }
      },
      parseMissingKeyHandler: (key, defaultValue, options) => {
        if (defaultValue === undefined) {
          return fallbackMessage;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const keyExists = i18next.exists(defaultValue, {
          ...options,
          ns: "default_validering",
        });

        if (keyExists) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return i18next.t(defaultValue, {
            ...options,
            ns: "default_validering",
          });
        } else {
          logFrontendToBackend(
            new Error(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
              `Missing default value for key - ${key} - default value ${defaultValue + ""}`,
            ),
            LoggLevel.ERROR,
          );
          return fallbackMessage;
        }
      },
    })
    .catch((err: unknown) => {
      console.error(err);
    });

  i18next.services.formatter?.add("fnr", (fnr: string): string => {
    return `${fnr.slice(0, 6)} ${fnr.slice(6)}`;
  });

  i18next.services.formatter?.add("orgnr", (orgnr: string): string => {
    return `${orgnr.slice(0, 3)} ${orgnr.slice(3, 6)} ${orgnr.slice(6)}`;
  });

  i18next.services.formatter?.add(
    "services_formatter",
    (serviceList: CheckName[]) => {
      const incidentTranslated = serviceList.map((incident) => {
        return i18next.t(`services.${incident}`, { ns: "incidents" });
      });
      if (incidentTranslated.length == 1) {
        return incidentTranslated[0];
      }
      const og = i18next.t("og", { ns: "incidents" });
      return (
        incidentTranslated.slice(0, -2).join(", ") +
        (incidentTranslated.length > 2 ? ", " : "") +
        incidentTranslated.slice(-2).join(` ${og} `)
      );
    },
  );

  i18next.services.formatter?.add(
    "anmeldelse_type_list_formatter",
    (anmeldelseTypeList: string) => {
      const liste: AnmeldelseType[] = anmeldelseTypeList // Format på streng: "[TYVERI_SKADEVERK_INNBRUDD,BEDRAGERI,...]"
        .slice(1, -1) //Fjerner klammeparenteser fra starten og slutten: "TYVERI_SKADEVERK_INNBRUDD,BEDRAGERI,..."
        .split(",") as AnmeldelseType[]; //Splitter på ,: ["TYVERI_SKADEVERK_INNBRUDD","BEDRAGERI",...]

      const translated = liste.map((anmeldelseType) => {
        return (
          "\n- " +
          i18next.t(`felles.anmeldelseType.${anmeldelseType}`, {
            ns: "skjematekst",
          })
        );
      });
      return translated.join("");
    },
  );

  i18next.services.formatter?.add(
    "generic_string_list_formatter",
    (stringList?: string) => {
      if (stringList == undefined) return "";
      const liste: string[] = stringList // Format på streng: "[element1,element2,...]"
        .slice(1, -1) //Fjerner klammeparentes "element1,element2,..."
        .split(",") //Splitter på ,: ["element1","element2",...])
        .map((value) => `\n -${value}`); // Legger på linjeshift
      return liste.join("");
    },
  );
};
